import React, { useState, useEffect, useCallback } from 'react';

import * as FileService from '../services/FileService.mjs';
import { useConfirm } from '../context/ConfirmContext.jsx';
import { notify } from '../notifications/errorBus';

import '../css/FileLockBadge.css';

/**
 * "Someone is working on this."
 *
 * Binary assets don't merge — two people uploading versions of the same `.psd`
 * is unrecoverable afterwards, so the only useful moment to intervene is
 * BEFORE the second person starts. That makes this badge's real job showing up
 * where a person is about to act, not being a complete lock-management UI.
 *
 * The lock is advisory. This deliberately offers no "break it" button for a
 * peer: the desktop client's push already asks and can force, and putting a
 * one-click override in the browser would make breaking a colleague's lock the
 * path of least resistance. Admins get the release, because the eight-hour
 * expiry isn't fast enough when someone leaves for a fortnight.
 *
 * See PROJECTS_SYNC_PLAN.md §10.
 */

const SKIP = { skipGlobalErrorToast: true };

const errMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

/** "3 hours ago", roughly — precision past that is noise for this. */
function sinceLabel(iso) {
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then)) return '';
    const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    return hours < 24 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`;
}

const FileLockBadge = ({ file, canAdmin = false, onChanged }) => {
    const confirm = useConfirm();
    const [lock, setLock] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setLock(await FileService.getFileLock(file.id, SKIP));
        } catch {
            // A lock we can't read is not worth an error banner — the file is
            // still perfectly usable, and the badge simply doesn't appear.
            setLock(null);
        }
    }, [file.id]);

    useEffect(() => { load(); }, [load]);

    const take = async () => {
        setBusy(true);
        try {
            setLock(await FileService.acquireFileLock(file.id, {}, SKIP));
            onChanged?.();
        } catch (err) {
            notify({
                title: 'Could not take the lock',
                message: errMessage(err, 'Someone else may have taken it first.'),
            });
            await load();
        } finally {
            setBusy(false);
        }
    };

    const release = async () => {
        if (lock && !lock.mine) {
            const ok = await confirm({
                title: `Release ${lock.userName}'s lock?`,
                message: 'They may be part-way through an edit. Locks also expire on their own within 8 hours.',
                confirmText: 'Release it',
                danger: true,
            });
            if (!ok) return;
        }

        setBusy(true);
        try {
            await FileService.releaseFileLock(file.id, SKIP);
            setLock(null);
            onChanged?.();
        } catch (err) {
            notify({ title: 'Could not release the lock', message: errMessage(err, 'Please try again.') });
        } finally {
            setBusy(false);
        }
    };

    if (!lock) {
        return (
            <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={take} disabled={busy}>
                {busy ? 'Locking…' : 'Lock while I work on it'}
            </button>
        );
    }

    return (
        <div className={`flb ${lock.mine ? 'flb--mine' : 'flb--theirs'}`}>
            <span className="flb-dot" aria-hidden="true" />
            <span className="flb-text">
                <strong>{lock.mine ? 'You have this locked' : `${lock.userName} is working on this`}</strong>
                <span className="flb-meta">
                    {sinceLabel(lock.since)}
                    {lock.machine ? ` · ${lock.machine}` : ''}
                    {lock.note ? ` · ${lock.note}` : ''}
                </span>
            </span>
            {(lock.mine || canAdmin) && (
                <button type="button" className="qd-btn qd-btn--ghost qd-btn--sm" onClick={release} disabled={busy}>
                    {lock.mine ? 'Release' : 'Break lock'}
                </button>
            )}
        </div>
    );
};

export default FileLockBadge;
